/**
 * GraduatedSymbologyPanel
 *
 * UI component for configuring graduated (range-based) symbology.
 * Allows users to select a numeric attribute field, choose a classification
 * method, set the number of classes, and select a color ramp.
 *
 * **Validates: Requirements 42.1, 42.3, 42.7**
 */

import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Slider,
  List,
  ListItem,
  ListItemText,
  Divider,
  Stack,
  Tooltip,
  Paper,
} from '@mui/material';
import { BarChart } from '@mui/icons-material';
import type { GraduatedRule, ClassificationMethod, ColorRamp } from '../types/symbology';
import { PREDEFINED_COLOR_RAMPS } from '../types/symbology';
import {
  generateGraduatedRules,
  getNumericValues,
  interpolateColorRamp,
} from '../services/SymbologyEngine';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface GraduatedSymbologyPanelProps {
  /** Available numeric attribute field names. */
  fields: string[];
  /** Features with their properties for extracting numeric values. */
  features: Array<{ properties: Record<string, any> }>;
  /** Currently selected field (if any). */
  selectedField?: string;
  /** Current graduated rules (if any). */
  currentRules?: GraduatedRule[];
  /** Called when the user applies the graduated symbology. */
  onApply: (
    field: string,
    rules: GraduatedRule[],
    method: ClassificationMethod,
    numClasses: number,
    colorRampId: string
  ) => void;
  /** Called when the user cancels. */
  onCancel: () => void;
}

// ---------------------------------------------------------------------------
// Color ramp preview
// ---------------------------------------------------------------------------

interface ColorRampPreviewProps {
  ramp: ColorRamp;
  numClasses: number;
  selected?: boolean;
  onClick?: () => void;
}

const ColorRampPreview: React.FC<ColorRampPreviewProps> = ({
  ramp,
  numClasses,
  selected,
  onClick,
}) => {
  const colors = interpolateColorRamp(ramp, numClasses);
  return (
    <Tooltip title={ramp.name}>
      <Box
        onClick={onClick}
        sx={{
          display: 'flex',
          height: 20,
          borderRadius: 1,
          overflow: 'hidden',
          cursor: onClick ? 'pointer' : 'default',
          border: selected ? '2px solid' : '1px solid',
          borderColor: selected ? 'primary.main' : 'divider',
          flex: 1,
        }}
        role="button"
        aria-label={`Color ramp: ${ramp.name}`}
        aria-pressed={selected}
      >
        {colors.map((color, i) => (
          <Box key={i} sx={{ flex: 1, backgroundColor: color }} />
        ))}
      </Box>
    </Tooltip>
  );
};

// ---------------------------------------------------------------------------
// Classification method labels
// ---------------------------------------------------------------------------

const CLASSIFICATION_METHODS: {
  value: ClassificationMethod;
  label: string;
  description: string;
}[] = [
  {
    value: 'equal-interval',
    label: 'Equal Interval',
    description: 'Divides range into equal-width intervals',
  },
  {
    value: 'quantile',
    label: 'Quantile',
    description: 'Each class has approximately the same number of values',
  },
  {
    value: 'natural-breaks',
    label: 'Natural Breaks (Jenks)',
    description: 'Minimizes within-class variance',
  },
  {
    value: 'standard-deviation',
    label: 'Standard Deviation',
    description: 'Classes based on standard deviations from mean',
  },
];

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const GraduatedSymbologyPanel: React.FC<GraduatedSymbologyPanelProps> = ({
  fields,
  features,
  selectedField: initialField,
  currentRules,
  onApply,
  onCancel,
}) => {
  const [selectedField, setSelectedField] = useState<string>(initialField || '');
  const [method, setMethod] = useState<ClassificationMethod>('equal-interval');
  const [numClasses, setNumClasses] = useState<number>(5);
  const [colorRampId, setColorRampId] = useState<string>('blues');
  const [rules, setRules] = useState<GraduatedRule[]>(currentRules || []);

  // Filter to only numeric fields
  const numericFields = useMemo(() => {
    return fields.filter((field) => {
      const values = getNumericValues(features, field);
      return values.length > 0;
    });
  }, [fields, features]);

  // Regenerate rules when parameters change
  useEffect(() => {
    if (selectedField) {
      const newRules = generateGraduatedRules(
        features,
        selectedField,
        method,
        numClasses,
        colorRampId
      );
      setRules(newRules);
    }
  }, [selectedField, method, numClasses, colorRampId, features]);

  // Statistics for the selected field
  const stats = useMemo(() => {
    if (!selectedField) return null;
    const values = getNumericValues(features, selectedField);
    if (values.length === 0) return null;
    const sorted = [...values].sort((a, b) => a - b);
    const sum = sorted.reduce((s, v) => s + v, 0);
    return {
      count: sorted.length,
      min: sorted[0],
      max: sorted[sorted.length - 1],
      mean: sum / sorted.length,
    };
  }, [features, selectedField]);

  // Apply
  const handleApply = useCallback(() => {
    if (selectedField && rules.length > 0) {
      onApply(selectedField, rules, method, numClasses, colorRampId);
    }
  }, [selectedField, rules, method, numClasses, colorRampId, onApply]);

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <BarChart color="primary" fontSize="small" />
        Graduated Symbology
      </Typography>

      {/* Field selector */}
      <FormControl fullWidth size="small" sx={{ mb: 2 }}>
        <InputLabel id="graduated-field-label">Numeric Attribute Field</InputLabel>
        <Select
          labelId="graduated-field-label"
          value={selectedField}
          label="Numeric Attribute Field"
          onChange={(e) => setSelectedField(e.target.value)}
          inputProps={{ 'aria-label': 'Select numeric attribute field' }}
        >
          {numericFields.map((field) => (
            <MenuItem key={field} value={field}>
              {field}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Classification method */}
      <FormControl fullWidth size="small" sx={{ mb: 2 }}>
        <InputLabel id="classification-method-label">Classification Method</InputLabel>
        <Select
          labelId="classification-method-label"
          value={method}
          label="Classification Method"
          onChange={(e) => setMethod(e.target.value as ClassificationMethod)}
          inputProps={{ 'aria-label': 'Select classification method' }}
        >
          {CLASSIFICATION_METHODS.map((m) => (
            <MenuItem key={m.value} value={m.value}>
              <Box>
                <Typography variant="body2">{m.label}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {m.description}
                </Typography>
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Number of classes */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" gutterBottom>
          Number of Classes: {numClasses}
        </Typography>
        <Slider
          value={numClasses}
          onChange={(_, v) => setNumClasses(v as number)}
          min={2}
          max={20}
          step={1}
          marks={[
            { value: 2, label: '2' },
            { value: 5, label: '5' },
            { value: 10, label: '10' },
            { value: 15, label: '15' },
            { value: 20, label: '20' },
          ]}
          valueLabelDisplay="auto"
          aria-label="Number of classes"
        />
      </Box>

      {/* Color ramp selection */}
      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
        Color Ramp
      </Typography>
      <Box sx={{ mb: 2 }}>
        <Typography variant="caption" color="text.secondary" gutterBottom sx={{ display: 'block' }}>
          Sequential
        </Typography>
        <Stack spacing={0.5} sx={{ mb: 1 }}>
          {PREDEFINED_COLOR_RAMPS.filter((r) => r.type === 'sequential').map((ramp) => (
            <ColorRampPreview
              key={ramp.id}
              ramp={ramp}
              numClasses={numClasses}
              selected={colorRampId === ramp.id}
              onClick={() => setColorRampId(ramp.id)}
            />
          ))}
        </Stack>
        <Typography variant="caption" color="text.secondary" gutterBottom sx={{ display: 'block' }}>
          Diverging
        </Typography>
        <Stack spacing={0.5}>
          {PREDEFINED_COLOR_RAMPS.filter((r) => r.type === 'diverging').map((ramp) => (
            <ColorRampPreview
              key={ramp.id}
              ramp={ramp}
              numClasses={numClasses}
              selected={colorRampId === ramp.id}
              onClick={() => setColorRampId(ramp.id)}
            />
          ))}
        </Stack>
      </Box>

      {/* Statistics */}
      {stats && (
        <Paper variant="outlined" sx={{ p: 1, mb: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Field Statistics
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
            <Typography variant="caption">Count: {stats.count}</Typography>
            <Typography variant="caption">Min: {stats.min.toFixed(2)}</Typography>
            <Typography variant="caption">Max: {stats.max.toFixed(2)}</Typography>
            <Typography variant="caption">Mean: {stats.mean.toFixed(2)}</Typography>
          </Box>
        </Paper>
      )}

      {/* Class ranges preview */}
      {rules.length > 0 && (
        <>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Class Ranges
          </Typography>
          <List dense sx={{ maxHeight: 200, overflow: 'auto' }}>
            {rules.map((rule, index) => (
              <ListItem key={index} sx={{ py: 0.25 }}>
                <Box
                  sx={{
                    width: 20,
                    height: 20,
                    borderRadius: '3px',
                    backgroundColor: rule.symbol.fillColor,
                    border: '1px solid',
                    borderColor: 'divider',
                    mr: 1,
                    flexShrink: 0,
                  }}
                />
                <ListItemText primary={rule.label} primaryTypographyProps={{ variant: 'body2' }} />
              </ListItem>
            ))}
          </List>
        </>
      )}

      {/* Actions */}
      <Divider sx={{ my: 2 }} />
      <Stack direction="row" spacing={1} justifyContent="flex-end">
        <Button onClick={onCancel} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleApply}
          variant="contained"
          disabled={!selectedField || rules.length === 0}
        >
          Apply
        </Button>
      </Stack>
    </Box>
  );
};

export default GraduatedSymbologyPanel;
